import React, { useState, useEffect } from 'react';
import { Card, Button, Modal, Form, Input, Select, Row, Col, message, Tag, Divider, Upload, Empty, Spin, Image, Popconfirm } from 'antd';
import { ArrowLeftOutlined, UploadOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, DollarOutlined, DeleteOutlined, PlusOutlined, UserOutlined, MessageOutlined, SendOutlined, PlayCircleOutlined, DownloadOutlined } from '@ant-design/icons';
import { useNavigate, useParams } from 'react-router-dom';
import { orderApi } from '../services/api';

const { Option } = Select;
const { TextArea } = Input;

const styleOptions = ['仿真人', '2D动漫风格', '动态漫', '3D国创', '三渲二', '日式少女漫', '吉卜力'];

const statusColors = {
  pending: 'orange',
  accepted: 'blue',
  in_progress: 'processing',
  submitted: 'purple',
  approved: 'green',
  rejected: 'red',
  completed: 'gold',
  cancelled: 'default'
};

const statusNames = {
  pending: '待接单',
  accepted: '已接单',
  in_progress: '进行中',
  submitted: '已提交审核',
  approved: '审核通过',
  rejected: '审核失败',
  completed: '已完成',
  cancelled: '已取消'
};

const assetTypeOptions = [
  { value: '角色', label: '人物形象' },
  { value: '场景', label: '场景图片' },
  { value: '道具', label: '道具图片' },
  { value: '其他', label: '其他资产' }
];

const OrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [form] = Form.useForm();
  const [videoUrl, setVideoUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [comments, setComments] = useState([]);
  const [episodeComments, setEpisodeComments] = useState({});
  const [commentLoading, setCommentLoading] = useState({});
  const [assets, setAssets] = useState([]);
  const [assetType, setAssetType] = useState('角色');

  useEffect(() => {
    loadOrder();
    loadAssets();
    loadComments();
  }, [id]);

  const loadOrder = async () => {
    try {
      setLoading(true);
      const response = await orderApi.getById(id);
      setOrder(response.data.order);
    } catch (error) {
      message.error('加载订单失败');
    } finally {
      setLoading(false);
    }
  };

  const loadAssets = async () => {
    try {
      const response = await orderApi.getAssets(id);
      setAssets(response.data.assets || []);
    } catch (error) {
      console.error('加载资产失败:', error);
    }
  };

  const loadComments = async () => {
    try {
      const response = await orderApi.getComments(id);
      const allComments = response.data.comments || [];
      setComments(allComments);
      
      const grouped = {};
      allComments.forEach(comment => {
        if (comment.episode !== null && comment.episode !== undefined) {
          const key = String(comment.episode);
          if (!grouped[key]) grouped[key] = [];
          grouped[key].push(comment);
        }
      });
      setEpisodeComments(grouped);
    } catch (error) {
      console.error('加载评论失败:', error);
    }
  };

  const handleAddComment = async (episode) => {
    const inputId = `comment_input_${episode}`;
    const inputElement = document.getElementById(inputId);
    if (!inputElement || !inputElement.value.trim()) {
      message.warning('请输入回复内容');
      return;
    }
    try {
      setCommentLoading(prev => ({ ...prev, [episode]: true }));
      const response = await orderApi.addComment(id, { 
        content: inputElement.value.trim(),
        episode: episode
      });
      message.success('回复成功');
      inputElement.value = '';
      
      setEpisodeComments(prev => {
        const key = String(episode);
        const current = prev[key] || [];
        return { ...prev, [key]: [response.data.comment, ...current] };
      });
    } catch (error) {
      message.error(error.response?.data?.error || '回复失败');
    } finally {
      setCommentLoading(prev => ({ ...prev, [episode]: false }));
    }
  };

  const handleUploadAsset = async (file) => {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('asset_type', assetType);
      
      const response = await orderApi.uploadAsset(id, formData);
      message.success('资产上传成功');
      loadAssets();
      return false;
    } catch (error) {
      message.error(error.response?.data?.error || '上传失败');
      return false;
    }
  };

  const handleDeleteAsset = async (assetId) => {
    try {
      await orderApi.deleteAsset(id, assetId);
      message.success('资产删除成功');
      loadAssets();
    } catch (error) {
      message.error(error.response?.data?.error || '删除失败');
    }
  };

  const handleAccept = async () => {
    try {
      const response = await orderApi.accept(id);
      message.success('接单成功，项目已创建');
      setOrder(response.data.order);
      // 跳转到项目页面
      if (response.data.order.project) {
        navigate(`/project/${response.data.order.project.id}`);
      }
    } catch (error) {
      message.error(error.response?.data?.error || '接单失败');
    }
  };

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      const response = await orderApi.submit(id, {
        final_video_path: videoUrl
      });
      message.success('提交成功，等待审核');
      setOrder(response.data.order);
    } catch (error) {
      message.error(error.response?.data?.error || '提交失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveReviewComment = async () => {
    try {
      const values = await form.validateFields();
      
      let comment = '';
      if (order?.final_videos) {
        order.final_videos.forEach(video => {
          const episodeComment = values[`comment_${video.episode}`];
          if (episodeComment) {
            comment += `第${video.episode}集：${episodeComment}\n`;
          }
        });
      }
      if (values.comment) {
        comment += `\n整体意见：${values.comment}`;
      }
      
      await orderApi.saveReviewComment(id, { review_comment: comment.trim() });
      message.success('审核意见保存成功');
    } catch (error) {
      message.error(error.response?.data?.error || '保存失败');
    }
  };

  const handleReview = async (approved) => {
    try {
      setReviewing(true);
      const values = await form.validateFields();
      
      let comment = '';
      if (order?.final_videos) {
        order.final_videos.forEach(video => {
          const episodeComment = values[`comment_${video.episode}`];
          if (episodeComment) {
            comment += `第${video.episode}集：${episodeComment}\n`;
          }
        });
      }
      if (values.comment) {
        comment += `\n整体意见：${values.comment}`;
      }
      
      const response = await orderApi.review(id, {
        approved,
        comment: comment.trim() || values.comment || ''
      });
      message.success(approved ? '审核通过' : '审核失败');
      setOrder(response.data.order);
      form.resetFields();
    } catch (error) {
      message.error(error.response?.data?.error || '审核失败');
    } finally {
      setReviewing(false);
    }
  };

  const handleComplete = async () => {
    try {
      const response = await orderApi.complete(id);
      message.success('付款成功，订单完成');
      setOrder(response.data.order);
    } catch (error) {
      message.error(error.response?.data?.error || '付款失败');
    }
  };

  const handleCancel = async () => {
    try {
      const response = await orderApi.cancel(id);
      message.success('订单已取消');
      setOrder(response.data.order);
    } catch (error) {
      message.error(error.response?.data?.error || '取消失败');
    }
  };

  const handleDownloadVideo = (videoPath, episode) => {
    const link = document.createElement('a');
    link.href = `/${videoPath}`;
    link.download = `episode_${episode}.mp4`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;
  }

  if (!order) {
    return <Empty description="订单不存在" />;
  }

  const user = JSON.parse(localStorage.getItem('user'));
  const userId = Number(user?.id);
  const isCompany = Number(order.company_user?.id) === userId;
  const isFreelancer = Number(order.freelancer_user?.id) === userId;

  return (
    <div style={{ padding: 24, background: '#0d0d12', minHeight: '100%' }}>
      <Button
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate('/')}
        style={{ marginBottom: 24, color: '#00d4aa' }}
      >
        返回首页
      </Button>

      <Card
        style={{ background: '#1a1a24', border: '1px solid #2a2a3a', marginBottom: 24 }}
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ color: '#e8e8ec' }}>{order.title}</span>
            <Tag color={statusColors[order.status]}>{statusNames[order.status]}</Tag>
          </div>
        }
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <ClockCircleOutlined style={{ color: '#00d4aa' }} />
              <span style={{ color: '#e8e8ec' }}>时长: {order.duration_minutes} 分钟</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <DollarOutlined style={{ color: '#00d4aa' }} />
              <span style={{ color: '#e8e8ec' }}>单价: {order.price_per_minute} 积分/分钟</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ color: '#e8e8ec', fontWeight: 'bold', fontSize: 18 }}>总价: {order.total_price} 积分</span>
            </div>
          </Col>
          <Col xs={24} sm={12}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ color: '#a0a0b0' }}>风格:</span>
              <Tag color="#00d4aa">{order.style}</Tag>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <span style={{ color: '#a0a0b0' }}>交付周期:</span>
              <span style={{ color: '#e8e8ec' }}>{order.delivery_days} 天</span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ color: '#a0a0b0' }}>预期交付:</span>
              <span style={{ color: '#e8e8ec' }}>{order.expected_delivery_date ? new Date(order.expected_delivery_date).toLocaleDateString() : '-'}</span>
            </div>
          </Col>
        </Row>

        <Divider style={{ borderColor: '#2a2a3a' }} />

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ color: '#e8e8ec', marginBottom: 8 }}>任务描述</h3>
          <p style={{ color: '#a0a0b0' }}>{order.description || '暂无描述'}</p>
        </div>

        <div style={{ marginBottom: 16 }}>
          <h3 style={{ color: '#e8e8ec', marginBottom: 8 }}>剧本内容</h3>
          <TextArea
            value={order.script_content}
            readOnly
            rows={6}
            style={{ background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
            placeholder="暂无剧本"
          />
        </div>

        {order.require_assets && (
          <div style={{ marginBottom: 16 }}>
            <h3 style={{ color: '#e8e8ec', marginBottom: 8 }}>资产要求</h3>
            <p style={{ color: '#a0a0b0' }}>{order.assets_description || '暂无说明'}</p>
          </div>
        )}

        {(order.require_assets || assets.length > 0) && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ color: '#e8e8ec', marginBottom: 0 }}>资产文件</h3>
              {isCompany && (
                <div style={{ display: 'flex', gap: 8 }}>
                  <Select
                    value={assetType}
                    onChange={setAssetType}
                    style={{ width: 120, background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
                  >
                    {assetTypeOptions.map(opt => (
                      <Option key={opt.value} value={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                  <Upload
                    beforeUpload={handleUploadAsset}
                    accept="image/*"
                    showUploadList={false}
                  >
                    <Button icon={<PlusOutlined />} style={{ background: '#00d4aa', borderColor: '#00d4aa' }}>
                      上传资产
                    </Button>
                  </Upload>
                </div>
              )}
            </div>
            {assets.length > 0 ? (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16 }}>
                {assets.map(asset => (
                  <div key={asset.id} style={{ position: 'relative', width: 160, height: 120 }}>
                    <Image
                      src={`/${asset.file_path}`}
                      alt={asset.file_name}
                      style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: 8 }}
                    />
                    <div style={{ position: 'absolute', top: 4, left: 4, background: 'rgba(0,0,0,0.6)', padding: '2px 8px', borderRadius: 4 }}>
                      <span style={{ color: '#fff', fontSize: 12 }}>{asset.asset_type}</span>
                    </div>
                    {isCompany && (
                      <Popconfirm
                        title="确定删除该资产？"
                        onConfirm={() => handleDeleteAsset(asset.id)}
                        okText="确定"
                        cancelText="取消"
                      >
                        <Button
                          icon={<DeleteOutlined />}
                          size="small"
                          danger
                          style={{ position: 'absolute', bottom: 4, right: 4 }}
                        />
                      </Popconfirm>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <Empty description="暂无资产文件" />
            )}
          </div>
        )}

        <Divider style={{ borderColor: '#2a2a3a' }} />

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ color: '#a0a0b0' }}>发布者: </span>
            <span style={{ color: '#e8e8ec' }}>{order.company_user?.nickname}</span>
          </div>
          {order.freelancer_user && (
            <div>
              <span style={{ color: '#a0a0b0' }}>接单者: </span>
              <span style={{ color: '#e8e8ec' }}>{order.freelancer_user?.nickname}</span>
            </div>
          )}
        </div>
      </Card>



      {isCompany && order.status !== 'pending' && order.status !== 'completed' && order.status !== 'cancelled' && (
        <Card
          style={{ background: '#1a1a24', border: '1px solid #2a2a3a', marginBottom: 24 }}
          title={<span style={{ color: '#e8e8ec' }}>成片审核</span>}
        >
          {order.final_videos && order.final_videos.length > 0 ? (
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16, marginBottom: 24 }}>
              {order.final_videos.map(video => (
                <div key={video.id} style={{ background: '#252532', borderRadius: 8, overflow: 'hidden' }}>
                  <div style={{ padding: 8, borderBottom: '1px solid #2a2a3a' }}>
                    <span style={{ color: '#e8e8ec', fontSize: 14, fontWeight: 'bold' }}>第 {video.episode || '?'} 集</span>
                  </div>
                  <div style={{ position: 'relative', paddingBottom: '56.25%' }}>
                    <video
                      src={`/${video.video_path}`}
                      controls
                      style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                    />
                  </div>
                  <div style={{ padding: 8 }}>
                    <Form.Item name={`comment_${video.episode}`} label={<span style={{ color: '#a0a0b0', fontSize: 12 }}>审核意见</span>}>
                      <TextArea
                        rows={2}
                        placeholder="请输入本集修改意见"
                        style={{ background: '#1a1a24', borderColor: '#2a2a3a', color: '#e8e8ec', fontSize: 12 }}
                      />
                    </Form.Item>
                    
                    {(episodeComments[String(video.episode)] && episodeComments[String(video.episode)].length > 0) && (
                      <div style={{ padding: 8, background: '#1a1a24', borderRadius: 4, border: '1px solid #2a2a3a', marginBottom: 8 }}>
                        {episodeComments[String(video.episode)].map(comment => (
                          <div 
                            key={comment.id} 
                            style={{ 
                              marginBottom: 8,
                              display: 'flex',
                              gap: 8
                            }}
                          >
                            <div style={{ 
                              width: 24, 
                              height: 24, 
                              borderRadius: '50%',
                              background: comment.user.role === 'publisher' ? '#ff9800' : '#00d4aa',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              flexShrink: 0
                            }}>
                              <UserOutlined style={{ color: '#0d0d12', fontSize: 12 }} />
                            </div>
                            <div style={{ flex: 1 }}>
                              <div style={{ 
                                fontSize: 11, 
                                color: comment.user.role === 'publisher' ? '#ff9800' : '#00d4aa',
                                marginBottom: 2
                              }}>
                                {comment.user.nickname} ({comment.user.role === 'publisher' ? '发布者' : '创作者'})
                              </div>
                              <div style={{ 
                                fontSize: 12, 
                                color: '#e8e8ec',
                                whiteSpace: 'pre-wrap'
                              }}>
                                {comment.content}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div style={{ display: 'flex', gap: 8 }}>
                      <input
                        id={`comment_input_${video.episode}`}
                        type="text"
                        placeholder="回复创作者..."
                        style={{ 
                          flex: 1,
                          background: '#252532', 
                          border: '1px solid #2a2a3a', 
                          color: '#e8e8ec',
                          borderRadius: 4,
                          padding: '6px 12px',
                          fontSize: 12,
                          outline: 'none'
                        }}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddComment(video.episode);
                          }
                        }}
                      />
                      <Button
                        type="primary"
                        icon={<SendOutlined />}
                        size="small"
                        loading={commentLoading[video.episode]}
                        onClick={() => handleAddComment(video.episode)}
                        style={{ 
                          background: '#00d4aa', 
                          borderColor: '#00d4aa', 
                          color: '#0d0d12',
                          padding: '0 12px'
                        }}
                      >
                        发送
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ padding: 40, textAlign: 'center', color: '#a0a0b0' }}>
              <PlayCircleOutlined style={{ fontSize: 48, marginBottom: 16 }} />
              <p>暂无成片视频</p>
            </div>
          )}

          <Form form={form} layout="vertical">
            <Form.Item name="comment" label="整体审核意见">
              <TextArea
                rows={3}
                placeholder="请输入整体审核意见，如果审核不通过请说明修改要求"
                style={{ background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec' }}
              />
            </Form.Item>
            <Form.Item>
              <Button
                type="primary"
                icon={<SendOutlined />}
                onClick={handleSaveReviewComment}
                style={{ background: '#252532', borderColor: '#2a2a3a', color: '#e8e8ec', marginRight: 16 }}
              >
                保存意见
              </Button>
              <Button
                type="primary"
                icon={<CheckCircleOutlined />}
                loading={reviewing}
                onClick={() => handleReview(true)}
                style={{ background: '#00d4aa', borderColor: '#00d4aa', marginRight: 16 }}
              >
                审核通过并付款
              </Button>
              <Button
                icon={<CloseCircleOutlined />}
                loading={reviewing}
                onClick={() => handleReview(false)}
                danger
              >
                审核不通过
              </Button>
            </Form.Item>
          </Form>
        </Card>
      )}

      {order.review_comment && (
        <Card
          style={{ background: '#1a1a24', border: '1px solid #2a2a3a', marginBottom: 24 }}
          title={<span style={{ color: '#e8e8ec' }}>审核反馈</span>}
        >
          <p style={{ color: '#a0a0b0' }}>{order.review_comment}</p>
        </Card>
      )}

      {(isCompany || isFreelancer) && order.status === 'completed' && order.final_videos && order.final_videos.length > 0 && (
        <Card
          style={{ background: '#1a1a24', border: '1px solid #2a2a3a', marginBottom: 24 }}
          title={<span style={{ color: '#e8e8ec' }}>成片下载</span>}
        >
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 16 }}>
            {order.final_videos.map(video => (
              <div key={video.id} style={{ background: '#252532', borderRadius: 8, overflow: 'hidden' }}>
                <div style={{ padding: 8, borderBottom: '1px solid #2a2a3a' }}>
                  <span style={{ color: '#e8e8ec', fontSize: 14, fontWeight: 'bold' }}>第 {video.episode || '?'} 集</span>
                </div>
                <div style={{ position: 'relative', paddingBottom: '56.25%' }}>
                  <video
                    src={`/${video.video_path}`}
                    controls
                    style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }}
                  />
                </div>
                <div style={{ padding: 8, display: 'flex', justifyContent: 'center' }}>
                  <Button
                    icon={<DownloadOutlined />}
                    onClick={() => handleDownloadVideo(video.video_path, video.episode)}
                    style={{ background: '#00d4aa', borderColor: '#00d4aa', color: '#0d0d12' }}
                  >
                    下载成片
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}
      
      
      <div style={{ marginTop: 24, display: 'flex', gap: 16 }}>
        {order.status === 'pending' && !isCompany && (
          <Button
            type="primary"
            onClick={handleAccept}
            style={{ background: '#00d4aa', borderColor: '#00d4aa' }}
          >
            接单
          </Button>
        )}
        {order.status === 'approved' && isCompany && (
          <Button
            type="primary"
            onClick={handleComplete}
            style={{ background: '#00d4aa', borderColor: '#00d4aa' }}
          >
            确认付款
          </Button>
        )}
        {(order.status === 'pending' || order.status === 'accepted') && isCompany && (
          <Button
            onClick={handleCancel}
            danger
          >
            取消订单
          </Button>
        )}
      </div>
    </div>
  );
};

export default OrderDetailPage;